import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Employees
export const employeesAPI = {
  getAll: (params = {}) => api.get('/employees', { params }).then(res => {
    // Support both paginated and direct array responses
    if (Array.isArray(res.data)) return res;
    return { ...res, data: res.data.items || [] };
  }),
  getPaginated: (params = {}) => api.get('/employees', { params }),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  updateWorkStatus: (id, status) => api.put(`/employees/${id}/work-status`, { work_status: status }),
  getWorkStatus: (id) => api.get(`/employees/${id}/work-status`),
  toggleActive: (id) => api.put(`/employees/${id}/toggle-active`),
  setActive: (id, isActive) => api.put(`/employees/${id}/set-active`, { is_active: isActive }),
  exportCSV: () => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/employees/export/csv`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    });
  },
  importCSV: (fileContent) => api.post('/employees/import/csv', fileContent, {
    headers: { 'Content-Type': 'text/plain' }
  }),
};

// Offices
export const officesAPI = {
  getAll: () => api.get('/offices'),
  get: (id) => api.get(`/offices/${id}`),
  create: (data) => api.post('/offices', data),
  update: (id, data) => api.put(`/offices/${id}`, data),
  delete: (id) => api.delete(`/offices/${id}`),
  exportCSV: () => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/offices/export/csv`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `offices_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    });
  },
  importCSV: (fileContent) => api.post('/offices/import/csv', fileContent, {
    headers: { 'Content-Type': 'text/plain' }
  }),
};
// Certifications
export const certificationsAPI = {
  getAll: () => api.get('/certifications'),
  get: (id) => api.get(`/certifications/${id}`),
  create: (data) => api.post('/certifications', data),
  update: (id, data) => api.put(`/certifications/${id}`, data),
  delete: (id) => api.delete(`/certifications/${id}`),
  exportCSV: () => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/certifications/export/csv`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certifications_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    });
  },
  importCSV: (fileContent) => api.post('/certifications/import/csv', fileContent, {
    headers: { 'Content-Type': 'text/plain' }
  }),
};


// Customers
export const customersAPI = {
  getAll: (params = {}) => api.get('/customers', { params }).then(res => {
    // Support both paginated and direct array responses
    if (Array.isArray(res.data)) return res;
    return { ...res, data: res.data.items || [] };
  }),
  getPaginated: (params = {}) => api.get('/customers', { params }),
  getDropdownList: () => api.get('/customers/dropdown/list'),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  exportCSV: () => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/customers/export/csv`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    });
  },
  importCSV: (fileContent) => api.post('/customers/import/csv', fileContent, {
    headers: { 'Content-Type': 'text/plain' }
  }),
  removeDuplicates: () => api.post('/customers/remove-duplicates'),
  // Attachment APIs
  uploadAttachment: (customerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/customers/${customerId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAttachments: (customerId) => api.get(`/customers/${customerId}/attachments`),
  downloadAttachment: (customerId, attachmentId) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/customers/${customerId}/attachments/${attachmentId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.blob());
  },
  deleteAttachment: (customerId, attachmentId) => 
    api.delete(`/customers/${customerId}/attachments/${attachmentId}`),
};

export const jobsAPI = {
  getAll: (params = {}) => api.get('/jobs/all', { params }),  // Use /jobs/all for unpaginated results
  generateNumber: () => api.get('/jobs/generate-number'),
  getPaginated: (params = {}) => api.get('/jobs', { params }),
  get: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  checkConflicts: ({ employee_ids, start_time, end_time, exclude_job_id }) => 
    api.get('/jobs/check-conflicts', { 
      params: { 
        employee_ids: employee_ids.join(','), 
        start_time, 
        end_time, 
        exclude_job_id 
      } 
    }),
  // Recurring series management
  getSeries: (seriesId) => api.get(`/jobs/series/${seriesId}`),
  deleteSeries: (seriesId, futureOnly = true) => api.delete(`/jobs/series/${seriesId}`, { params: { future_only: futureOnly } }),
  updateSeries: (seriesId, data, futureOnly = true) => api.put(`/jobs/series/${seriesId}`, data, { params: { future_only: futureOnly } }),
  extendSeries: (seriesId, additionalCount = 4) => api.post(`/jobs/series/${seriesId}/extend`, null, { params: { additional_count: additionalCount } }),
  exportCSV: (params) => {
    // For CSV download, we need to handle it differently
    const query = new URLSearchParams(params).toString();
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/jobs/export?${query}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jobs_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    });
  },
  getCalendarFeedUrl: () => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/calendar/feed?token=${token}`;
  },
  // Attachment APIs
  uploadAttachment: (jobId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/jobs/${jobId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAttachments: (jobId) => api.get(`/jobs/${jobId}/attachments`),
  downloadAttachment: (jobId, attachmentId) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/jobs/${jobId}/attachments/${attachmentId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.blob());
  },
  deleteAttachment: (jobId, attachmentId) => 
    api.delete(`/jobs/${jobId}/attachments/${attachmentId}`),
};

// Personal Events removed

// Availability
export const availabilityAPI = {
  get: (userId) => api.get('/availability', { params: { user_id: userId } }),
  set: (data) => api.post('/availability', data),
  getAll: () => api.get('/availability/all'),
  bulkSetAll: () => api.post('/availability/bulk-set-all'),
};

// Time Off
export const timeOffAPI = {
  getAll: () => api.get('/time-off'),
  request: (data) => api.post('/time-off', data),
  approve: (id) => api.put(`/time-off/${id}/approve`),
  reject: (id, reason = '') => api.put(`/time-off/${id}/reject`, { reason }),
  delete: (id) => api.delete(`/time-off/${id}`),
};

// Notifications
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  // Notification preferences
  getPrefs: () => api.get('/notifications/preferences'),
  updatePrefs: (data) => api.put('/notifications/preferences', data),
  // Service status (admin)
  getServiceStatus: () => api.get('/notifications/service-status'),
  getLogs: () => api.get('/notifications/logs'),
};

// Dashboard
export const dashboardAPI = {
  getStats: (costCenter = null) => api.get('/analytics/dashboard', { params: { cost_center: costCenter } }),
  getCostCenters: () => api.get('/analytics/cost-centers'),
};

// Analytics
export const analyticsAPI = {
  getJobsByStatus: (costCenter = null) => api.get('/analytics/jobs-by-status', { params: { cost_center: costCenter } }),
  getJobsOverTime: (days = 30, costCenter = null) => api.get('/analytics/jobs-over-time', { params: { days, cost_center: costCenter } }),
  getEmployeeHours: (days = 7) => api.get('/analytics/employee-hours', { params: { days } }),
  getTimeTrackingSummary: (days = 30) => api.get('/analytics/time-tracking-summary', { params: { days } }),
  getShiftSwapsSummary: () => api.get('/analytics/shift-swaps-summary'),
};

// Shift Swaps
export const shiftSwapsAPI = {
  getAll: (params = {}) => api.get('/shift-swaps', { params }),
  get: (id) => api.get(`/shift-swaps/${id}`),
  create: (data) => api.post('/shift-swaps', data),
  approve: (id) => api.put(`/shift-swaps/${id}/approve`),
  reject: (id, reason) => api.put(`/shift-swaps/${id}/reject`, null, { params: { reason } }),
  cancel: (id) => api.delete(`/shift-swaps/${id}`),
  deletePermanently: (id) => api.delete(`/shift-swaps/${id}/permanent`),
  clearAll: () => api.delete('/shift-swaps'),
  getMyJobs: () => api.get('/shift-swaps/my-jobs'),
  getAvailableEmployees: (jobId) => api.get('/shift-swaps/available-employees', { params: { job_id: jobId } }),
};

// GPS Time Entries
export const timeEntriesAPI = {
  clockIn: (data) => api.post('/time-entries/clock-in', data),
  clockOut: (data) => api.post('/time-entries/clock-out', data),
  getCurrent: () => api.get('/time-entries/current'),
  getAll: (params = {}) => api.get('/time-entries', { params }),
  get: (id) => api.get(`/time-entries/${id}`),
  getByJob: (jobId) => api.get(`/jobs/${jobId}/time-entries`),
  delete: (id) => api.delete(`/time-entries/${id}`),
};

// Time Tracking (Admin-managed time entries)
export const timeTrackingAPI = {
  getEntries: (params = {}) => api.get('/time-tracking/entries', { params }),
  getSummary: (params = {}) => api.get('/time-tracking/summary', { params }),
  getEmployeeHoursSummary: () => api.get('/time-tracking/employee-hours-summary'),
  getAllJobs: () => api.get('/time-tracking/all-jobs'),
  getAllEmployees: () => api.get('/time-tracking/all-employees'),
};

// Team Chat
export const chatAPI = {
  // Channels
  getChannels: () => api.get('/chat/channels'),
  createChannel: (data) => api.post('/chat/channels', data),
  updateChannel: (channelId, data) => api.put(`/chat/channels/${channelId}`, data),
  deleteChannel: (channelId) => api.delete(`/chat/channels/${channelId}`),
  getChannelMessages: (channelId, params = {}) => api.get(`/chat/channels/${channelId}/messages`, { params }),
  sendChannelMessage: (channelId, data) => api.post(`/chat/channels/${channelId}/messages`, data),
  
  // Direct Messages
  getConversations: () => api.get('/chat/direct-messages'),
  getDMMessages: (partnerId, params = {}) => api.get(`/chat/direct-messages/${partnerId}`, { params }),
  sendDirectMessage: (data) => api.post('/chat/direct-messages', data),
  
  // Users
  getUsers: () => api.get('/chat/users'),
  
  // Files
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Polling
  poll: (lastCheck) => api.get('/chat/poll', { params: { last_check: lastCheck } }),
  
  // Init
  initChannels: () => api.post('/chat/init-channels'),
};

export default api;
